import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { CalendarIcon, Loader2Icon } from 'lucide-react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { NbButton, NbCard, NbPageHeader } from '@/components/Neobrut';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  API_BASE_URL,
  CAT_RACES,
  DOG_RACES,
  PUBLISH_PET_FIELDS_TRANSLATIONS,
  PET_AGES_LABELS,
  PET_COLORS,
} from '@/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import LocationPicker from '@/components/Map/LocationPicker';
import { Disclaimer } from '@/components/Disclaimer/Disclaimer';
import { useAuthToken } from '@/hooks/useAuthToken';
import { ImageUploader } from '@/components/ImageUploader/ImageUploader';

interface FormValues {
  status: PetStatus;
  type: PetType;
  dateFoundOrLost: Date;
  name?: string;
  sterilized: boolean;
  gender: PetGender;
  age: PetAges;
  mainColor: PetColor;
  race?: string;
  location: LocationIQReverseGeocodingResponse;
  retained: boolean;
  observations?: string;
  images: FileList;
}

export const Publish = () => {
  const { token, isTokenExpired } = useAuthToken();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const { register, control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      status: 'lost',
      type: 'cat',
      sterilized: false,
      age: 'baby',
      dateFoundOrLost: new Date(),
      gender: 'm',
      name: '',
      observations: '',
      retained: false,
    },
  });

  const status = watch('status');
  const type = watch('type');

  const [locationPickerVisibility, setLocationPickerVisibility] = useState(false);
  const [locationData, setLocationData] = useState<LocationIQReverseGeocodingResponse | null>(null);

  useEffect(() => {
    if (!token || isTokenExpired(token)) {
      navigate('/login');
    }
  }, [token, isTokenExpired, navigate]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      if (!data.images || (data.images && data.images.length === 0)) {
        console.error('No images selected');
        setErrors(['Debes seleccionar al menos una imagen']);
        return;
      }

      if (data.images.length > 4) {
        setErrors(['Puedes subir un máximo de 4 imágenes']);
        return;
      }

      // Validate name if status is 'lost'
      if (data.status === 'lost' && (!data.name || data.name.length < 2)) {
        setErrors(['Ingrese el nombre de la mascota']);
        return;
      }

      // Validate location
      if (!data.location) {
        setErrors(['Debes seleccionar una ubicación']);
        return;
      }

      // @TODO Custom validation logic, change this to use zod
      const isValid = Object.values(data).every((value) => value !== undefined && value !== null);
      if (!isValid) {
        console.error('Form is invalid');
        setErrors(['Debes completar todos los campos']);
        return;
      }

      setIsLoading(true);

      // Step 1: Create the pet
      const dateKey = data.status === 'found' ? 'date_found' : 'date_lost';

      if (!token || isTokenExpired(token)) {
        toast.info('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        navigate('/login');
        return;
      }

      const petResponse = await fetch(`${API_BASE_URL}/pets/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: data.status,
          type: data.type,
          [dateKey]: format(data.dateFoundOrLost, 'yyyy-MM-dd', { locale: enUS }),
          sterilized: data.sterilized,
          retained: data.retained,
          name: data.name,
          gender: data.gender,
          age: data.age,
          main_color: data.mainColor,
          race: data.race,
          location: data.location,
          observations: data.observations,
        }),
      });

      if (!petResponse.ok) {
        setIsLoading(false);
        if (petResponse.status === 400) {
          const errorData = await petResponse.json();
          setErrors(
            Object.entries(errorData).map(
              ([key, value]) =>
                `${PUBLISH_PET_FIELDS_TRANSLATIONS[key as keyof Omit<Pet, 'solved_case'>]}: ${value}`,
            ),
          );
        }
        throw new Error('Falla al crear publicación');
      }

      const petData = await petResponse.json();
      const petId = petData.id;

      const formData = new FormData();
      formData.set('pet', petId);
      Array.from(data.images).forEach((image) => {
        formData.append('images', image);
      });

      // Step 2: Upload images
      const imageResponse = await fetch(`${API_BASE_URL}/pet-gallery/bulk-upload/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!imageResponse.ok) {
        setIsLoading(false);
        setErrors(['Error al subir las imágenes']);
        throw new Error('Falla al subir las imágenes');
      }

      toast.success('Publicación creada exitosamente');
      setIsLoading(false);
      navigate(data.status === 'found' ? '/found' : '/lost');
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl lg:max-w-3xl pb-10">
      <NbPageHeader
        slug="/ publicar"
        title={
          <>
            Publicá
            <br />
            <span className="nb-highlight">en 2 minutos.</span>
          </>
        }
        subtitle="Contanos lo básico de la mascota. Después podés editar."
        tone="plain"
      />

      <div className="px-4 pt-4">
        <Disclaimer />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="px-4 pt-4">
        <NbCard className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <label className="font-bold">Esta mascota fue:</label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex w-full flex-row gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="lost" id="lost" />
                  <Label htmlFor="lost">Perdida</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="found" id="found" />
                  <Label htmlFor="found">Encontrada</Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-bold">Tipo:</label>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex w-full flex-row gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cat" id="cat" />
                  <Label htmlFor="cat">Gato</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dog" id="dog" />
                  <Label htmlFor="dog">Perro</Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-bold">Sexo:</label>
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex w-full flex-row gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="m" id="m" />
                  <Label htmlFor="m">Macho</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="f" id="f" />
                  <Label htmlFor="f">Hembra</Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="sterilized" className="font-bold">
            ¿Esterilizada?
          </label>
          <Controller
            name="sterilized"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="sterilized"
                  className="h-5 w-5"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <label htmlFor="sterilized" className="text-sm">
                  <div>Marcar si la mascota está esterilizada</div>
                  <div className="text-gray-500">(si no lo sabe dejar en blanco)</div>
                </label>
              </div>
            )}
          />
        </div>

        {/* Fecha */}
        <div className="flex flex-col gap-1">
          <label htmlFor="dateFoundOrLost" className="font-bold">
            Fecha:
          </label>
          <Controller
            name="dateFoundOrLost"
            control={control}
            render={({ field }) => (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 w-full justify-start">
                    {field.value
                      ? format(field.value, 'dd/MM/yyyy', { locale: es })
                      : 'Selecciona una fecha'}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={(date) => field.onChange(date)}
                    initialFocus
                    toDate={new Date()}
                  />
                </PopoverContent>
              </Popover>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-bold">Edad:</label>
          <Controller
            name="age"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange}>
                <SelectTrigger className="h-10 w-full bg-white font-medium">
                  <SelectValue placeholder="Selecciona una edad" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PET_AGES_LABELS).map(([key, value]) => (
                    <SelectItem key={key} value={key}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div key={'name'} className={'flex flex-col gap-1'}>
          <label htmlFor={'name'} className={`font-bold ${status === 'found' && 'text-gray-500'}`}>
            Nombre:
          </label>
          <input
            type="text"
            id={'name'}
            className={`rounded-md p-2 ${status === 'found' && 'border-2 border-solid border-black'}`}
            {...register('name')}
            disabled={status === 'found'}
          />
        </div>

        <div key={'location'} className={'flex flex-col gap-1'}>
          <label htmlFor={'location'} className="font-bold">
            Ubicación:
          </label>
          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <>
                <input
                  type="text"
                  id={'location'}
                  placeholder="Selecciona una ubicación"
                  className={`rounded-md p-2`}
                  {...register('location')}
                  value={
                    locationData
                      ? `${locationData.address.road} ${locationData.address.house_number}, ${locationData.address.city}, ${locationData.address.state}`
                      : ''
                  }
                  onFocus={() => setLocationPickerVisibility(true)}
                  readOnly
                />
                <LocationPicker
                  open={locationPickerVisibility}
                  onSubmit={(data) => {
                    setLocationData(data);
                    field.onChange(data);
                    setLocationPickerVisibility(false);
                  }}
                  onClose={() => setLocationPickerVisibility(false)}
                />
              </>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="retained" className={`font-bold ${status === 'lost' && 'text-gray-500'}`}>
            ¿Retenida?
          </label>
          <Controller
            name="retained"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="retained"
                  className="h-5 w-5"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={status === 'lost'}
                />
                <label
                  htmlFor="retained"
                  className={`text-sm ${status === 'lost' && 'text-gray-500'}`}
                >
                  Tildar si la mascota está retenida
                </label>
              </div>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-bold">Raza:</label>
          <Controller
            name="race"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange}>
                <SelectTrigger className="h-10 w-full bg-white font-medium">
                  <SelectValue placeholder="Selecciona una raza" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    className="border-b-[1px] border-slate-400"
                    key={'Mestizo'}
                    value="mestizo"
                  >
                    Mestizo
                  </SelectItem>
                  {type === 'cat'
                    ? Object.entries(CAT_RACES).map(([key, value]) => (
                        <SelectItem key={key} value={value}>
                          {value}
                        </SelectItem>
                      ))
                    : Object.entries(DOG_RACES).map(([key, value]) => (
                        <SelectItem key={key} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div key={'mainColor'} className="flex flex-col gap-1">
          <label htmlFor={'mainColor'} className="font-bold">
            Color:
          </label>
          <Controller
            name="mainColor"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-10 w-full bg-white font-medium">
                  <SelectValue placeholder="Seleccionar color" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PET_COLORS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div key={'observations'} className="flex flex-col gap-1">
          <label htmlFor={'observations'} className="font-bold">
            Observaciones:
          </label>
          <textarea
            rows={4}
            placeholder="Escribe aquí tus observaciones"
            maxLength={200}
            minLength={10}
            id={'observations'}
            className="rounded-md p-2"
            {...register('observations')}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="images" className="font-bold">
            Imágenes:
          </Label>
          <p className="text-sm text-gray-500">
            Selecciona hasta 4 imágenes de la mascota. (Recomendado: 1 imagen de la cara y 1 del
            cuerpo)
          </p>
          <Controller
            name="images"
            control={control}
            render={({ field }) => (
              <ImageUploader
                onImagesSelected={(images: File[]) => {
                  field.onChange(images);
                }}
              />
            )}
          />
        </div>

          <NbButton
            fill="ink"
            type="submit"
            disabled={isLoading}
            className="w-full py-3"
          >
            {isLoading && <Loader2Icon className="h-4 w-4 animate-spin" />}
            Publicar
          </NbButton>
        </NbCard>
      </form>

      {errors.length > 0 && (
        <div
          className="mx-4 mt-3 p-3"
          style={{ border: '2px solid #b42020', background: '#fff0f0' }}
        >
          <div
            className="text-[10px] font-bold uppercase tracking-[0.08em]"
            style={{ fontFamily: '"JetBrains Mono", monospace', color: '#b42020' }}
          >
            / errores
          </div>
          <ul className="mt-1 list-inside list-disc text-[12px]" style={{ color: '#b42020' }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
